import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Les variables d\'environnement Supabase sont manquantes');
}

// Configuration du client Supabase avec retry et timeout
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: localStorage,
      storageKey: 'sb-auth-token',
      flowType: 'pkce'
    },
    global: {
      headers: { 'x-application-name': 'conciergerie' }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Vérification de session améliorée avec retry
export const getSession = async (retryCount = 3) => {
  for (let i = 0; i < retryCount; i++) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.warn(`Tentative ${i + 1}/${retryCount} - Erreur de session:`, error);
        if (i === retryCount - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
        continue;
      }

      return session;
    } catch (error) {
      console.error('Erreur lors de la vérification de la session:', error);
      if (i === retryCount - 1) return null;
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  return null;
};

// Déconnexion sécurisée avec nettoyage
export const signOut = async () => {
  try {
    // Clear local storage first
    localStorage.removeItem('sb-auth-token');
    localStorage.removeItem('supabase.auth.token');
    
    // Clear role cache
    const currentUser = (await supabase.auth.getUser()).data.user;
    if (currentUser?.id) {
      localStorage.removeItem(`user-role-${currentUser.id}`);
    }
    
    // Then attempt to sign out from Supabase
    const { error } = await supabase.auth.signOut({
      scope: 'local' // Only clear local session
    });
    
    if (error) {
      console.warn('Warning during signout:', error);
    }
    
    return true;
  } catch (error) {
    console.error('Erreur de déconnexion:', error);
    // Even if there's an error, we've cleared local storage
    return true;
  }
};

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password.trim()
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Email ou mot de passe incorrect');
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Erreur de connexion:', error);
    throw error;
  }
};

// Récupération du rôle utilisateur avec cache et retry
export const getUserRole = async (userId: string, bypassCache = false, retryCount = 3): Promise<string | null> => {
  try {
    // Check cache first if not bypassing
    if (!bypassCache) {
      const cachedRole = localStorage.getItem(`user-role-${userId}`);
      if (cachedRole) {
        return cachedRole;
      }
    }

    for (let i = 0; i < retryCount; i++) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', userId)
          .single();

        if (error) {
          console.warn(`Tentative ${i + 1}/${retryCount} - Erreur de récupération du rôle:`, error);
          if (i === retryCount - 1) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
          continue;
        }

        if (data?.role) {
          // Cache the role for 5 minutes
          localStorage.setItem(`user-role-${userId}`, data.role);
          setTimeout(() => {
            localStorage.removeItem(`user-role-${userId}`);
          }, 5 * 60 * 1000);
          
          return data.role;
        }
      } catch (error) {
        if (i === retryCount - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du rôle:', error);
    return null;
  }
};

export const setupAuthListener = (callback: (session: any) => void) => {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session);
  });
};

// Fonction utilitaire pour vérifier la connexion
export const checkConnection = async (): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .select('count', { count: 'exact', head: true });
    
    return !error;
  } catch (error) {
    console.error('Erreur de connexion Supabase:', error);
    return false;
  }
};

// Fonction utilitaire pour obtenir le statut de connexion
export const getConnectionStatus = async () => {
  try {
    const { error } = await supabase
      .from('profiles')
      .select('count', { count: 'exact', head: true });
    
    return {
      isConnected: !error,
      error: error ? error.message : null
    };
  } catch (error: any) {
    return {
      isConnected: false,
      error: error.message || 'Erreur de connexion'
    };
  }
};